/**
 * ConfirmDeleteModal component for confirming destructive actions
 * Provides a consistent modal interface for delete confirmations
 */

import { AlertTriangle, X, Loader2 } from 'lucide-react';
import * as React from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

// ============================================================================
// TYPES
// ============================================================================

export interface ConfirmDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  itemName?: string;
  variant?: 'danger' | 'warning';
}

// ============================================================================
// MODAL OVERLAY COMPONENT
// ============================================================================

function ModalOverlay({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClick();
        }
      }}
    >
      {children}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ConfirmDeleteModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = 'Delete',
  cancelText = 'Cancel',
  isLoading = false,
  itemName,
  variant = 'danger',
}: ConfirmDeleteModalProps) {
  const [isConfirming, setIsConfirming] = React.useState(false);
  const modalRef = React.useRef<HTMLDivElement>(null);

  // Reset confirming state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setIsConfirming(false);
    }
  }, [isOpen]);

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isLoading && !isConfirming) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isLoading, isConfirming, onClose]);

  // Focus trap
  React.useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm();
    } finally {
      setIsConfirming(false);
    }
  };

  if (!isOpen) return null;

  const variantStyles = {
    danger: {
      icon: 'bg-danger-soft text-danger',
      iconColor: 'text-danger',
    },
    warning: {
      icon: 'bg-warning-soft text-warning',
      iconColor: 'text-warning',
    },
  };

  const styles = variantStyles[variant];

  const modalContent = (
    <ModalOverlay onClick={onClose}>
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        tabIndex={-1}
        className={cn(
          'w-full max-w-md bg-surface rounded-xl shadow-xl border border-border-subtle',
          'animate-in zoom-in-95 slide-in-from-bottom-2 duration-200',
          'focus:outline-none'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-4 p-6">
          {/* Icon */}
          <div
            className={cn(
              'flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center',
              styles.icon
            )}
          >
            <AlertTriangle className={cn('h-6 w-6', styles.iconColor)} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h2
              id="modal-title"
              className="text-lg font-semibold text-primary leading-tight"
            >
              {title}
            </h2>
            <p
              id="modal-description"
              className="mt-2 text-sm text-secondary leading-relaxed"
            >
              {description}
            </p>
            {itemName && (
              <p className="mt-3 text-sm font-medium text-primary bg-surface-subtle rounded-md px-3 py-2 border border-border-subtle">
                &quot;{itemName}&quot;
              </p>
            )}
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            disabled={isLoading || isConfirming}
            className={cn(
              'flex-shrink-0 p-1 rounded-md text-muted hover:text-primary hover:bg-surface-subtle transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/50',
              'disabled:opacity-50 disabled:pointer-events-none'
            )}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 pb-6">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isLoading || isConfirming}
          >
            {cancelText}
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            disabled={isLoading || isConfirming}
            leftIcon={
              isLoading || isConfirming ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : undefined
            }
          >
            {isLoading || isConfirming ? 'Deleting...' : confirmText}
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );

  return createPortal(modalContent, document.body);
}
